import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { Animated, View } from 'react-native';
import { BLACK_700, BLACK_900 } from '../../constants';
import { useBreakpoint } from '../../hooks';
import { StateConfigContext } from '../../providers';
import { getTestId } from '../../utils';
import { Typography } from '../typography';
import { applyStep, getSliderProps, getSliderStyles } from './SliderStyles';
import { Input } from '../input';
export var Slider = forwardRef((props, ref) => {
  var _a, _b;
  var stateContext = useContext(StateConfigContext);
  var sliderProps = getSliderProps(props, stateContext);
  var _useBreakpoint = useBreakpoint({
      returnAsObject: true
    }),
    isSmallScreen = _useBreakpoint.isSmallScreen;
  var sliderStyles = getSliderStyles(sliderProps, isSmallScreen);
  var _sliderProps$value = sliderProps.value,
    value = _sliderProps$value === void 0 ? 0 : _sliderProps$value,
    startText = sliderProps.startText,
    endText = sliderProps.endText,
    hintText = sliderProps.hintText,
    config = sliderProps.config,
    isDisabled = sliderProps.isDisabled,
    headingTitle = sliderProps.headingTitle,
    _sliderProps$minimumV = sliderProps.minimumValue,
    minimumValue = _sliderProps$minimumV === void 0 ? 0 : _sliderProps$minimumV,
    _sliderProps$maximumV = sliderProps.maximumValue,
    maximumValue = _sliderProps$maximumV === void 0 ? 1 : _sliderProps$maximumV,
    showInput = sliderProps.showInput,
    inputProps = sliderProps.inputProps,
    onValueChange = sliderProps.onValueChange,
    style = sliderProps.style,
    stepSize = sliderProps.stepSize,
    _sliderProps$testID = sliderProps.testID,
    testID = _sliderProps$testID === void 0 ? 'Slider' : _sliderProps$testID;
  var sliderRef = useRef(ref);
  var mobileConfig = (_a = config === null || config === void 0 ? void 0 : config.platform) === null || _a === void 0 ? void 0 : _a.mobile;
  var desktopConfig = (_b = config === null || config === void 0 ? void 0 : config.platform) === null || _b === void 0 ? void 0 : _b.desktop;
  var thumbSize = isSmallScreen ? mobileConfig === null || mobileConfig === void 0 ? void 0 : mobileConfig.thumbSize : desktopConfig === null || desktopConfig === void 0 ? void 0 : desktopConfig.thumbSize;
  var defaultValue = thumbSize / 2;
  var _useState = useState(-defaultValue),
    sliderLocationX = _useState[0],
    setSliderLocationX = _useState[1];
  var _useState2 = useState(value),
    _inputValue = _useState2[0],
    setInputValue = _useState2[1];
  useEffect(() => {
    setInputValue(value);
  }, [value]);
  useEffect(() => {
    updateValueHandler(_inputValue);
  }, [_inputValue]);
  var updateValueHandler = value => {
    var _value = value;
    if (sliderRef.current) {
      if (typeof _value !== 'number') _value = 0;
      if (value > maximumValue) _value = maximumValue;
      if (_value < minimumValue) _value = minimumValue;
      sliderRef.current.measure((_x, _y, sliderWidth) => {
        if (sliderWidth === 0) {
          setTimeout(() => updateValueHandler(_value), 0);
        } else {
          var absValue = (_value - minimumValue) / (maximumValue - minimumValue) * sliderWidth - defaultValue;
          setSliderLocationX(absValue);
        }
      });
    }
  };
  var onMoveHandler = event => {
    if (sliderRef.current && event) {
      var _ref = (event === null || event === void 0 ? void 0 : event.nativeEvent) || {},
        _ref$pageX = _ref.pageX,
        pageX = _ref$pageX === void 0 ? 0 : _ref$pageX;
      sliderRef.current.measure(function (_x, _y, sliderWidth, _sliderHeight, sliderPageX) {
        if (sliderPageX === void 0) {
          sliderPageX = 0;
        }
        var currentValue = pageX - sliderPageX - defaultValue;
        if (currentValue <= -defaultValue) {
          currentValue = -defaultValue;
        }
        if (currentValue + defaultValue >= sliderWidth) {
          currentValue = sliderWidth - defaultValue;
        }
        setSliderLocationX(currentValue);
        var absValue = minimumValue + (currentValue + defaultValue) / sliderWidth * (maximumValue - minimumValue);
        var clampedValue = Math.min(Math.max(absValue, minimumValue), maximumValue);
        var steppedValue = applyStep(clampedValue, stepSize);
        var finalValue = minimumValue === 0 && maximumValue === 1 ? parseFloat(steppedValue.toFixed(2)) : steppedValue;
        setInputValue(finalValue);
        onValueChange(finalValue);
      });
    }
  };
  return _jsxs(View, Object.assign({}, getTestId(testID), {
    style: style
  }, {
    children: [showInput && _jsxs(View, Object.assign({
      style: sliderStyles.fieldContainer
    }, {
      children: [_jsx(Typography, {
        type: "body-single-line-" + (isSmallScreen ? 'md' : 'lg') + "-bold",
        text: headingTitle,
        color: BLACK_900
      }), _jsx(Input, Object.assign({}, inputProps, {
        value: _inputValue.toString(),
        onChangeText: text => {
          var _a;
          (_a = inputProps === null || inputProps === void 0 ? void 0 : inputProps.onChangeText) === null || _a === void 0 ? void 0 : _a.call(inputProps, text);
          var newValue = parseFloat(text);
          if (!isNaN(newValue)) {
            setInputValue(applyStep(newValue, stepSize));
          }
        },
        size: isSmallScreen ? 'md' : 'lg'
      }, getTestId(testID, 'sliderInput')))]
    })), !!(startText || !!endText) && _jsxs(View, Object.assign({
      style: sliderStyles.startEndTextContainer
    }, {
      children: [!!startText && _jsx(Typography, {
        type: "body-single-line-" + (isSmallScreen ? 'xs' : 'sm') + "-bold",
        text: startText
      }), !!endText && _jsx(Typography, {
        style: sliderStyles.endText,
        type: "body-single-line-" + (isSmallScreen ? 'xs' : 'sm') + "-bold",
        text: endText
      })]
    })), _jsx(View, Object.assign({
      style: sliderStyles.containerWrapper,
      accessibilityActions: [{
        name: 'increment',
        label: 'increment'
      }, {
        name: 'decrement',
        label: 'decrement'
      }],
      accessible: true,
      accessibilityRole: 'adjustable',
      onStartShouldSetResponder: () => !isDisabled,
      onMoveShouldSetResponder: () => !isDisabled,
      onResponderMove: onMoveHandler,
      onResponderStart: onMoveHandler
    }, {
      children: _jsxs(View, Object.assign({
        ref: sliderRef,
        style: sliderStyles.container,
        pointerEvents: "none"
      }, {
        children: [_jsx(Animated.View, {
          pointerEvents: "none",
          style: [sliderStyles.thumb, {
            left: sliderLocationX
          }]
        }), _jsx(Animated.View, {
          pointerEvents: "none",
          style: [sliderStyles.track, {
            width: sliderLocationX + defaultValue
          }]
        })]
      }))
    })), !!hintText && _jsx(Typography, {
      type: "body-para-" + (isSmallScreen ? 'xs' : 'sm'),
      text: hintText,
      color: BLACK_700
    })]
  }));
});